import { startTransition, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Box, Typography } from "@mui/material";
import { SecondaryButton } from "@shared/CustomButton";
import { StyledCheckbox } from "@shared/form/CheckboxField";
import { QueryBuilderField } from "@shared/form/QueryBuilderField/QueryBuilderField";
import { useVirtualizer } from "@tanstack/react-virtual";

import { DeviceFormKey, getSelectedDeviceIds, toDeviceKey } from "../CreateRolloutSidePanel.utils";

import { DeviceRow, GroupRow, rowEstimateSize } from "./RolloutTreeRows";
import { useStyles } from "./RolloutUnifiedTree.styles";
import {
    filterTreeBySearch,
    flattenVisibleTree,
    FlatTreeItem,
    getAllDeviceIdsInTree,
    getAllGroupTreeNodeIds,
    getTotalDeviceCountInTree,
    GroupTreeNode,
    searchDevicesInTree,
    SearchResult,
} from "./RolloutUnifiedTree.utils";

type Props = {
    tree: GroupTreeNode[];
    values: Record<string, unknown> | undefined;
    onChange: (id: DeviceFormKey, value: boolean) => void;
    onBatchChange?: (updates: Array<{ id: DeviceFormKey; value: boolean }>) => void;
    codename: string | undefined;
};

export const RolloutUnifiedTree = ({ tree, values, onChange, onBatchChange, codename }: Props) => {
    const { classes, cx } = useStyles();
    const { t } = useTranslation(["sidepanels", "common"]);
    const containerRef = useRef<HTMLDivElement>(null);

    const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());
    const [searchQuery, setSearchQuery] = useState("");

    const searchResult: SearchResult = useMemo(() => searchDevicesInTree(tree, searchQuery), [tree, searchQuery]);
    const isSearching = Boolean(searchQuery.trim());

    const displayTree = useMemo(() => {
        if (!isSearching) {
            return tree;
        }
        if (searchResult.matchingDeviceIds.size === 0) {
            return [];
        }
        return filterTreeBySearch(tree, searchResult.groupsToExpand);
    }, [tree, isSearching, searchResult]);

    const flatList = useMemo(
        () => flattenVisibleTree(displayTree, expandedGroups, searchResult),
        [displayTree, expandedGroups, searchResult],
    );

    const selectedDeviceIds = useMemo(() => new Set(getSelectedDeviceIds(values)), [values]);
    const totalDeviceCount = useMemo(() => getTotalDeviceCountInTree(tree), [tree]);
    const allDeviceIds = useMemo(() => getAllDeviceIdsInTree(tree), [tree]);

    const scopedDeviceIds = useMemo(
        () => (isSearching ? Array.from(searchResult.matchingDeviceIds) : allDeviceIds),
        [isSearching, searchResult.matchingDeviceIds, allDeviceIds],
    );
    const scopedTotal = isSearching ? searchResult.matchingDeviceIds.size : totalDeviceCount;
    const scopedSelectedCount = useMemo(
        () => scopedDeviceIds.reduce((count, id) => (selectedDeviceIds.has(id) ? count + 1 : count), 0),
        [scopedDeviceIds, selectedDeviceIds],
    );

    const allSelected = scopedTotal > 0 && scopedSelectedCount === scopedTotal;
    const someSelected = scopedSelectedCount > 0 && scopedSelectedCount < scopedTotal;

    const onToggleExpand = useCallback((groupId: number) => {
        setExpandedGroups((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(groupId)) {
                newSet.delete(groupId);
            } else {
                newSet.add(groupId);
            }
            return newSet;
        });
    }, []);

    const onExpandAll = useCallback(() => {
        startTransition(() => {
            setExpandedGroups(new Set(getAllGroupTreeNodeIds(tree)));
        });
    }, [tree]);

    const onCollapseAll = useCallback(() => {
        startTransition(() => {
            setExpandedGroups(new Set());
        });
    }, []);

    const onSelectAllChange = useCallback(() => {
        const shouldSelect = !allSelected;
        if (onBatchChange && scopedDeviceIds.length > 1) {
            onBatchChange(scopedDeviceIds.map((deviceId) => ({ id: toDeviceKey(deviceId), value: shouldSelect })));
        } else {
            scopedDeviceIds.forEach((deviceId) => {
                onChange(toDeviceKey(deviceId), shouldSelect);
            });
        }
    }, [allSelected, scopedDeviceIds, onBatchChange, onChange]);

    const onClearSearch = useCallback(() => {
        setSearchQuery("");
    }, []);

    const getItemKey = useCallback(
        (index: number) => {
            const item = flatList[index];
            if (!item) {
                return index;
            }
            if (item.type === "group") {
                return `group-${item.group.id}`;
            }
            return `device-${item.device.id}`;
        },
        [flatList],
    );

    const virtualizer = useVirtualizer({
        count: flatList.length,
        getScrollElement: () => containerRef.current,
        estimateSize: () => rowEstimateSize,
        overscan: 5,
        getItemKey,
    });

    useEffect(() => {
        if (searchResult.groupsToExpand.size > 0) {
            setExpandedGroups((prev) => new Set([...prev, ...searchResult.groupsToExpand]));
        }
    }, [searchResult.groupsToExpand]);

    const hasCompatibleDevices = tree.length > 0;
    const isEmpty = displayTree.length === 0;

    return (
        <Box className={classes.root}>
            <Box className={classes.searchField}>
                <QueryBuilderField
                    value={searchQuery}
                    onChange={setSearchQuery}
                    onClear={onClearSearch}
                    placeholder={t("common:button.search")}
                    debounceWait={300}
                />
            </Box>

            {isSearching && searchResult.matchingDeviceIds.size > 0 && (
                <Typography variant="caption" className={classes.matchInfo}>
                    {t("sidepanels:rolloutPanel.matchingDevices", { count: searchResult.matchingDeviceIds.size })}
                </Typography>
            )}

            {hasCompatibleDevices && (
                <Box className={classes.toolbar}>
                    <Box className={classes.selectAllBox} onClick={onSelectAllChange}>
                        <StyledCheckbox
                            checked={allSelected}
                            indeterminate={someSelected}
                            onChange={onSelectAllChange}
                            slotProps={{ input: { "aria-label": t("sidepanels:rolloutPanel.aria.selectAll") } }}
                        />
                        <Typography variant="body2">
                            {t("sidepanels:rolloutPanel.selectAllCount", {
                                selected: scopedSelectedCount,
                                total: scopedTotal,
                            })}
                        </Typography>
                    </Box>
                    <SecondaryButton size="small" onClick={expandedGroups.size > 0 ? onCollapseAll : onExpandAll}>
                        {expandedGroups.size > 0
                            ? t("sidepanels:rolloutPanel.collapseAll")
                            : t("sidepanels:rolloutPanel.expandAll")}
                    </SecondaryButton>
                </Box>
            )}

            <Box ref={containerRef} className={cx(classes.container, { [classes.emptyContainer]: isEmpty })}>
                {isEmpty ? (
                    <Typography variant="body2" color="text.secondary" className={classes.emptyState}>
                        {isSearching
                            ? t("sidepanels:rolloutPanel.noDevicesMatching", { query: searchQuery })
                            : t("sidepanels:rolloutPanel.noCompatibleDevices", { codename: codename ?? "" })}
                    </Typography>
                ) : (
                    <Box className={classes.virtualListInner} style={{ height: virtualizer.getTotalSize() }}>
                        {virtualizer.getVirtualItems().map((virtualRow) => {
                            const item = flatList[virtualRow.index] as FlatTreeItem | undefined;
                            if (!item) {
                                return null;
                            }

                            if (item.type === "group") {
                                return (
                                    <Box
                                        key={virtualRow.key}
                                        className={classes.virtualRow}
                                        style={{ transform: `translateY(${virtualRow.start}px)` }}
                                    >
                                        <GroupRow
                                            group={item.group}
                                            depth={item.depth}
                                            expandedGroups={expandedGroups}
                                            selectedDeviceIds={selectedDeviceIds}
                                            onToggleExpand={onToggleExpand}
                                            onChange={onChange}
                                            onBatchChange={onBatchChange}
                                        />
                                    </Box>
                                );
                            }

                            return (
                                <Box
                                    key={virtualRow.key}
                                    className={classes.virtualRow}
                                    style={{ transform: `translateY(${virtualRow.start}px)` }}
                                >
                                    <DeviceRow
                                        device={item.device}
                                        depth={item.depth}
                                        isSelected={selectedDeviceIds.has(item.device.id)}
                                        isHighlighted={searchResult.matchingDeviceIds.has(item.device.id)}
                                        onChange={onChange}
                                    />
                                </Box>
                            );
                        })}
                    </Box>
                )}
            </Box>
        </Box>
    );
};
